import Fade from '@mui/material/Fade'
import StarIcon from '@mui/icons-material/Star'
import clsx from 'clsx'
import { ReactComponent as DropdownIcon } from 'images/icons/arrow_select.svg'
import { ReactComponent as CopyIcon } from 'images/icons/copy.svg'
import { useId, useState } from 'react'
import getTestId from 'utils/getTestId'

import { StyledDropdown } from './StyledDropdown'

import style1 from './Dropdown.module.css'
import style2 from './SmallDropdown.module.css'

export interface IOption {
  value: string | number | null | undefined
  text: string | number
  /** Leading column in the open menu (e.g. serial); aligned with monospace/`ch` width. */
  leadingText?: string
  /** Gold star before the label (Favorites filter row and its closed trigger). */
  leadingIcon?: 'star'
  subtitle?: string
  isCurrent?: boolean
  isDbCurrent?: boolean
}

function optionTriggerLabel(option: IOption): string {
  const leading = option.leadingText?.trim() || ''
  const text = String(option.text ?? '').trim()
  if (leading && text && leading !== text) {
    return `${leading} · ${text}`
  }
  return leading || text
}

export interface IPropsDropdown {
  options: IOption[]
  currentOption?: IOption
  placeholder?: string
  disabled?: boolean
  onClickOption: (option: IOption) => void
  dataTestId?: string
  className?: string
  description?: string
  useSmallDropdownStyle?: boolean
  minWidth?: number | string
  /** Keep trigger width; expand the open menu to fit option labels. */
  menuFitContent?: boolean
  menuMaxWidth?: number | string
  style?: React.CSSProperties
  noShadow?: boolean
  canCopy?: boolean
  descriptionClassName?: string
}

const Dropdown: React.FC<IPropsDropdown> = ({
  options,
  onClickOption = () => {},
  placeholder = '',
  currentOption = null,
  disabled = false,
  dataTestId = 'dropdown',
  description,
  className,
  useSmallDropdownStyle = false,
  minWidth,
  menuFitContent = false,
  menuMaxWidth = 'min(420px, 90vw)',
  style: inlineStyle,
  noShadow = false,
  canCopy = false,
  descriptionClassName,
}) => {
  const [open, setOpen] = useState(false)
  const [copiedId, setCopiedId] = useState<string | number | null>(null)
  const reactId = useId()
  const triggerId = `${reactId}-trigger`
  const menuId = `${reactId}-menu`
  const labelId = description ? `${reactId}-label` : undefined
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const style = useSmallDropdownStyle ? style2 : style1

  const handleCopy = (event: React.MouseEvent, option: IOption) => {
    event.stopPropagation()
    navigator.clipboard.writeText(String(option.text))
    setCopiedId(option.value ?? null)
    setTimeout(() => setCopiedId(null), 1500)
  }

  const toggleOpen = () => {
    if (!disabled) {
      setOpen(prev => !prev)
      if (open) {
        setAnchorEl(null)
      }
    }
  }

  const handleClickDropdown = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(open ? null : event.currentTarget)
      toggleOpen()
    }
  }

  const handleClickOption = (option: IOption) => {
    if (!disabled) {
      onClickOption(option)
      setOpen(false)
      setAnchorEl(null)
    }
  }

  const dropdownWidth = anchorEl?.clientWidth
  const leadingCh = options.reduce((max, option) => {
    const len = (option.leadingText || '').trim().length
    return len > max ? len : max
  }, 0)
  const paperStyle = menuFitContent
    ? {
        minWidth: dropdownWidth ?? (minWidth || undefined),
        width: 'max-content',
        maxWidth: menuMaxWidth,
        ...(leadingCh > 0 ? ({ ['--dropdown-leading-ch' as string]: String(leadingCh) } as React.CSSProperties) : {}),
      }
    : {
        width: dropdownWidth ? dropdownWidth : undefined,
        minWidth: minWidth ? minWidth : undefined,
        ...(leadingCh > 0 ? ({ ['--dropdown-leading-ch' as string]: String(leadingCh) } as React.CSSProperties) : {}),
      }

  const triggerLabel = currentOption ? optionTriggerLabel(currentOption) : placeholder
  const accessibleName =
    description && triggerLabel && description !== triggerLabel
      ? `${description}: ${triggerLabel}`
      : triggerLabel || description || placeholder || 'Select'

  return (
    <>
      <div className={className} style={inlineStyle}>
        {description ? (
          <div id={labelId} className={clsx('text-14-400', style.subtitle, descriptionClassName)}>
            {description}
          </div>
        ) : null}
        <button
          type="button"
          id={triggerId}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          aria-label={accessibleName}
          className={clsx(style.placeholder, {
            [style.placeholderDisabled]: disabled,
            [style.noShadow]: noShadow,
          })}
          onClick={handleClickDropdown}
          data-test-id={getTestId(`${dataTestId}-placeholder`)}
        >
          <div
            className={clsx(style.placeholderText, {
              [style.placeholderTextWithStar]: currentOption?.leadingIcon === 'star',
            })}
            data-test-id={getTestId(`${dataTestId}-placeholderText`)}
          >
            {currentOption?.leadingIcon === 'star' ? <StarIcon className={style.optionStar} /> : null}
            <span className={style.placeholderLabel}>{triggerLabel}</span>
          </div>
          <div
            className={clsx(style.placeholderIcon, { [style.placeholderIconOpen]: open })}
            data-test-id={getTestId(`${dataTestId}-icon`)}
            aria-hidden
          >
            <DropdownIcon />
          </div>
        </button>
      </div>
      <StyledDropdown
        MenuListProps={{
          id: menuId,
          role: 'listbox',
          'aria-labelledby': triggerId,
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={() => {
          setOpen(false)
          setAnchorEl(null)
        }}
        TransitionComponent={Fade}
        container={typeof document !== 'undefined' ? () => document.body : undefined}
        disablePortal={false}
        disableEnforceFocus={true}
        disableRestoreFocus={true}
        PaperProps={{
          style: paperStyle,
        }}
        data-test-id={getTestId(`${dataTestId}-styledDropdown`)}
      >
        {options.map((option, index) => {
          const isActive = currentOption?.value === option.value
          const isCopied = copiedId === option.value
          return (
            <div
              key={index}
              role="option"
              aria-selected={isActive}
              tabIndex={-1}
              onClick={() => handleClickOption(option)}
            >
              {option.subtitle && (
                <div className={clsx(style.subtitle, menuFitContent && style.subtitleFit)}>{option.subtitle}</div>
              )}
              <div
                className={clsx(style.dropdownItem, {
                  [style.dropdownItemActive]: isActive,
                  [style.dropdownItemCurrent]: option.isCurrent,
                  [style.dropdownItemDbCurrent]: option.isDbCurrent,
                })}
                data-value={option.value}
                data-test-id={getTestId(`${dataTestId}-${option.value}`)}
              >
                <span
                  className={clsx('text-14-400', style.dropdownItemText, {
                    [style.dropdownItemTextFit]: menuFitContent,
                    [style.dropdownItemTextColumns]: Boolean(option.leadingText?.trim()),
                    [style.dropdownItemTextWithStar]: option.leadingIcon === 'star',
                  })}
                >
                  {option.leadingIcon === 'star' ? <StarIcon className={style.optionStar} /> : null}
                  {option.leadingText?.trim() ? (
                    <span className={style.dropdownItemLeading}>{option.leadingText.trim()}</span>
                  ) : null}
                  <span className={style.dropdownItemPrimary}>{option.text}</span>
                </span>
                {canCopy && (
                  <div className={style.copyIconWrapper} onClick={e => handleCopy(e, option)}>
                    <CopyIcon className={clsx(style.copyIcon, { [style.copyIconCopied]: isCopied })} />
                    {isCopied && <span className={style.copiedLabel}>Copied!</span>}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </StyledDropdown>
    </>
  )
}

export default Dropdown
